import { useAtomValue } from 'jotai';

import type { PermittedLocation } from '@cfa/pathway-fe-shared/xp';

import useAuth from '@/hooks/useAuth';
import usePlanOperators from '@/hooks/usePlanOperators';

import { activeLicenseeLocationAtom } from '@/atoms/licensee-location';

const UNKNOWN_OWNER_ID = 'UNKNOWN';

export type OwnerEligibility = (location: PermittedLocation) => boolean;

interface UsePlanOwnerOptions {
  /** Whether an auth location's owner can be used by this call site. */
  isEligible: OwnerEligibility;
}

export interface PlanOwnerOption {
  ownerId: string;
  ownerType: PermittedLocation['ownerType'];
  /** Display hint from the first auth location with this ownerId. */
  locationName?: string;
  locationNumber?: string;
  hasInterimManager: boolean;
}

export interface LabeledPlanOwnerOption extends PlanOwnerOption {
  label: string;
}

/**
 * Unique owners from auth locations. Ownership is pre-resolved by XP;
 * this only dedupes `ownerId` and keeps a label hint.
 */
const getUniquePlanOwners = (
  locations: PermittedLocation[],
): PlanOwnerOption[] => {
  const seen = new Set<string>();
  const owners: PlanOwnerOption[] = [];

  for (const entry of locations) {
    if (seen.has(entry.ownerId)) {
      continue;
    }
    seen.add(entry.ownerId);
    owners.push({
      ownerId: entry.ownerId,
      ownerType: entry.ownerType,
      locationName: entry.location.name,
      locationNumber: entry.location.number,
      hasInterimManager: entry.location.hasInterimManager,
    });
  }

  return owners;
};

export const ownerLabel = (
  owner: PlanOwnerOption,
  operatorNames: Map<string, string>,
  userFullName: string,
) => {
  if (owner.ownerType === 'OPERATOR') {
    return (
      operatorNames.get(owner.ownerId) ?? owner.locationName ?? owner.ownerId
    );
  }
  if (owner.ownerType === 'SELF') {
    return (
      [userFullName, owner.locationName].find(value => !!value) ?? owner.ownerId
    );
  }
  return owner.locationName ?? owner.ownerId;
};

const usePlanOwner = ({ isEligible }: UsePlanOwnerOptions) => {
  const { data: auth } = useAuth();
  const activeLicenseeLocation = useAtomValue(activeLicenseeLocationAtom);

  const eligibleLocations = (auth?.locationsWithRoles ?? []).filter(
    location => location.ownerId !== UNKNOWN_OWNER_ID && isEligible(location),
  );
  const planOwners = getUniquePlanOwners(eligibleLocations);
  const isLicensee = auth?.isLicensee;

  const activeLicenseeOwnerId = eligibleLocations.find(
    entry => entry.location.number === activeLicenseeLocation?.number,
  )?.ownerId;

  const isMultiOwner = planOwners.length > 1;
  const shouldSelectOwner = isMultiOwner && !isLicensee;

  /**
   * When `ownerId` is provided and is one of `planOwners`, echoes that selection.
   * When omitted or ineligible, uses the active licensee owner or the first auth owner.
   *
   * Licensees never fall through to another owner — if the active location
   * cannot be resolved to an eligible auth `ownerId` (missing, UNKNOWN, or
   * rejected by `isEligible`), returns an empty ownerId so callers fail
   * closed instead of attaching the plan to the wrong owner.
   */
  const resolvePlanOwner = (ownerId?: string) => {
    if (isLicensee) {
      return { ownerId: activeLicenseeOwnerId ?? '' };
    }

    if (ownerId && planOwners.some(owner => owner.ownerId === ownerId)) {
      return { ownerId };
    }

    const firstOwnerId = planOwners[0]?.ownerId;
    if (firstOwnerId) {
      return { ownerId: firstOwnerId };
    }

    return { ownerId: auth?.userId ?? '' };
  };

  return {
    isFetching: !auth?.userId,
    isMultiOwner,
    planOwners,
    resolvePlanOwner,
    shouldSelectOwner,
  };
};

/** `usePlanOwner` plus display labels for an owner picker. */
export const usePlanOwnerOptions = (options: UsePlanOwnerOptions) => {
  const { data: auth } = useAuth();
  const planOwner = usePlanOwner(options);
  const { operators } = usePlanOperators();

  const operatorNames = new Map(
    operators
      .filter(
        (operator): operator is { id: string; name: string } =>
          !!operator.id && !!operator.name,
      )
      .map(operator => [operator.id, operator.name]),
  );

  const ownerOptions: LabeledPlanOwnerOption[] = planOwner.planOwners.map(
    owner => ({
      ...owner,
      label: ownerLabel(owner, operatorNames, auth?.fullName ?? ''),
    }),
  );

  return { ...planOwner, ownerOptions };
};

export default usePlanOwner;
